import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Popover,
  Checkbox
} from '@mui/material';
import { Info, CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { useToolLibrary } from '../../../hooks/resources/useToolLibrary';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const ToolPicker = ({
  value,
  onChange,
  label = 'Select Tools',
  helperText = '',
  error = false,
  multiple = true,
  required = false
}) => {
  const { tools, loading, error: fetchError } = useToolLibrary();
  const [selectedTools, setSelectedTools] = useState(multiple ? [] : null);
  const [infoAnchor, setInfoAnchor] = useState(null);
  const [infoTool, setInfoTool] = useState(null);

  useEffect(() => {
    if (tools.length > 0) {
      if (multiple) {
        // Multi-select: value is array of toolIds
        const valueArray = value || [];
        const selected = tools.filter(t => valueArray.includes(t._id));
        setSelectedTools(selected);
      } else {
        // Single-select: value is a single toolId string or tool name
        const selected = tools.find(t => t._id === value || t.name === value) || null;
        setSelectedTools(selected);
      }
    } else if (multiple) {
      setSelectedTools([]);
    } else {
      setSelectedTools(null);
    }
  }, [value, tools, multiple]);

  const handleChange = (event, newValue) => {
    setSelectedTools(newValue);
    if (multiple) {
      onChange(newValue.map(t => t._id));
    } else {
      // Single selection - return tool name for transition trigger compatibility
      onChange(newValue?.name || '');
    }
  };

  const handleInfoClick = (event, tool) => {
    event.stopPropagation();
    setInfoTool(tool);
    setInfoAnchor(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoAnchor(null);
    setInfoTool(null);
  };

  return (
    <Box>
      <Autocomplete
        multiple={multiple}
        value={selectedTools}
        onChange={handleChange}
        options={tools}
        getOptionLabel={(option) => option?.displayName || option?.toolName || ''}
        loading={loading}
        disableCloseOnSelect={multiple}
        isOptionEqualToValue={(option, value) => option._id === value?._id}
        componentsProps={{
          popper: {
            sx: { zIndex: 1500 }
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={error || !!fetchError}
            helperText={fetchError?.message || helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option, { selected }) => {
          const { key, ...otherProps } = props;
          return (
            <Box component="li" key={key} {...otherProps} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {multiple && (
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  {option.displayName || option.toolName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.description}
                </Typography>
              </Box>
              {option.category && (
                <Chip
                  size="small"
                  label={option.category}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
              <IconButton
                size="small"
                onClick={(e) => handleInfoClick(e, option)}
                sx={{ ml: 1 }}
              >
                <Info fontSize="small" />
              </IconButton>
            </Box>
          );
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              label={option.displayName || option.toolName}
              {...getTagProps({ index })}
              size="small"
            />
          ))
        }
      />

      <Popover
        open={Boolean(infoAnchor)}
        anchorEl={infoAnchor}
        onClose={handleInfoClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {infoTool && (
          <Box sx={{ p: 2, maxWidth: 400 }}>
            <Typography variant="h6" gutterBottom>
              {infoTool.displayName || infoTool.toolName}
            </Typography>

            <Typography variant="body2" paragraph>
              {infoTool.description}
            </Typography>

            <Box sx={{ mt: 2 }}>
              {infoTool.category && (
                <Typography variant="caption" color="text.secondary" display="block">
                  <strong>Category:</strong> {infoTool.category}
                </Typography>
              )}
              {infoTool.parameters && infoTool.parameters.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    <strong>Parameters:</strong>
                  </Typography>
                  {infoTool.parameters.map((param, idx) => (
                    <Typography key={idx} variant="caption" color="text.secondary" display="block" sx={{ ml: 1 }}>
                      - {param.name} ({param.type}){param.required ? ' *required' : ''}
                    </Typography>
                  ))}
                </Box>
              )}
              {infoTool.usageCount && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  <strong>Usage:</strong> {infoTool.usageCount.flows} flows
                  {infoTool.usageCount.personas && `, ${infoTool.usageCount.personas} personas`}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default ToolPicker;
